import { useCallback, useMemo, useRef, useState } from 'react';
import { debounceWithAsyncFunc } from '../../utils/debounce';
import useAsyncCallback from './useAsyncCallback';

const recursiveCloneWithEmptyValues = (obj) => {
  return Object.keys(obj).reduce((acc, key) => {
    switch (typeof obj[key]) {
      case 'object':
        acc[key] = recursiveCloneWithEmptyValues(obj[key]);
        break;
      case 'string':
        acc[key] = '';
        break;
      default:
        acc[key] = null;
    }
    return acc;
  }, {});
};

export function useAutoCompleteField(name, getDataset, { initialValue, error, onChange }, cacheKeyPrefix = '') {
  // const [_field, meta, helpers] = useField(name);
  const [dataset, setDataset] = useState([]);
  const cache = useRef(null);

  if (!cache.current) {
    cache.current = {};
  }

  const getDatasetAsync = useAsyncCallback(getDataset, (data) => {
    cache.current[data.search] = data.dataset;
    setDataset(data.dataset);
  }, [getDataset]);
  const onSelect = useCallback(({ value }) => onChange(value), []);
  const onSearch = useCallback(debounceWithAsyncFunc((value) => {
    const search = value.trim().toLowerCase();
    const cachedValue = cache.current[cacheKeyPrefix + search];
    if (cachedValue) {
      setDataset(cachedValue);
      return Promise.resolve(cachedValue);
    }
    return getDatasetAsync(search);
  }, 600), [getDatasetAsync]);
  const onReset = useCallback(() => {
    let emptyValue;
    if (typeof initialValue === 'object') {
      emptyValue = recursiveCloneWithEmptyValues(initialValue);
    } else {
      emptyValue = typeof initialValue === 'string' ? '' : null;
    }
    onChange(emptyValue);
  }, []);

  return useMemo(() => ({
    dataset,
    onSelect,
    onSearch,
    onReset,
    name,
    state: error ? 'invalid' : undefined,
  }), [dataset, error, onSearch, onReset]);
}
